import CartService, { CART_CHANGE_EVENT, type CartChangeDetail } from '../Service/CartService'
import { trackViewCart } from '../Tracking/Ecommerce'
import { getElement, getElementChild, getElements } from '../Utility/Dom'
import { IS_HIDDEN } from '../Utility/State'

class CartButton {
  private readonly buttons: NodeListOf<HTMLElement>
  private readonly dialog: HTMLDialogElement | null

  constructor() {
    this.buttons = getElements('cart-button')
    this.dialog = getElement('cart-dialog') as HTMLDialogElement | null
  }

  public init(): void {
    if (this.buttons.length === 0) return

    this.buttons.forEach((button): void => {
      button.addEventListener('click', (): void => this.handleOpen())
    })

    document.addEventListener(CART_CHANGE_EVENT, (event: Event): void => {
      this.update((event as CustomEvent<CartChangeDetail>).detail.items.length)
    })

    this.update(CartService.getCount())
  }

  private getLabel(button: HTMLElement, count: number): string {
    const labelEmpty = button.dataset.labelEmpty ?? ''
    if (count === 0) return labelEmpty
    if (count === 1) return button.dataset.labelSingular ?? labelEmpty
    return (button.dataset.labelPlural ?? labelEmpty).replace('%s', count.toString())
  }

  private handleOpen(): void {
    if (this.dialog === null) return
    this.dialog.showModal()
    this.buttons.forEach((button): void => button.setAttribute('aria-expanded', 'true'))

    // The dialog is the cart view; a null cartId means it has not hydrated yet, so reporting it
    // would claim an empty cart the visitor may not have.
    const cart = CartService.getCart()
    if (cart.cartId !== null) trackViewCart(cart)
  }

  private update(count: number): void {
    this.buttons.forEach((button): void => this.updateButton(button, count))
  }

  private updateButton(button: HTMLElement, count: number): void {
    const badge = getElementChild(button, 'cart-button-count')
    if (badge !== null) {
      badge.textContent = count.toString()
      badge.classList.toggle(IS_HIDDEN, count === 0)
    }
    button.setAttribute('aria-label', this.getLabel(button, count))
  }
}

export default CartButton
